import { Api } from "../api";
import { AxiosResponse } from "axios";
// Used for JSDocs
// eslint-disable-next-line import/named, no-unused-vars
import { NotificationPlaceholder } from "./notificationService";

/**
 * @typedef {{
 *  id: number,
 *  municipalityId: number,
 *  municipalityName: string,
 *  statusId: number,
 *  statusName: string,
 *  assignedTo: string,
 *  openingBalance: number,
 *  closingBalance: number,
 *  allocation: number,
 *  interestEarned: number,
 *  disposals: number,
 *  netTransfers: number,
 *  netExpenditures: number,
 *  administrationCosts: number }} FinancialInformation
 */

/**
 * @typedef {{
 *  id: number,
 *  reconciliationId: number,
 *  municipalityId: number,
 *  municipalityName: string,
 *  isAudited: boolean,
 *  statusId: number,
 *  statusName: string,
 *  assignedTo: string,
 *  openingBalance: number,
 *  closingBalance: number,
 *  allocation: number,
 *  interestEarned: number,
 *  disposals: number,
 *  netTransfers: number,
 *  netExpenditures: number,
 *  administrationCosts: number }} ReconciliationInformationDataObject
 */

/**
 * @typedef {{
 *    year: number,
 *    openBalance: number,
 *    allocation: number,
 *    transferredIn: number,
 *    transferredOut: number,
 *    interestEarned: number,
 *    proceedsFromDisposal: number,
 *    closeBalance: number,
 *    airport: number,
 *    broadband: number,
 *    brownfield: number,
 *    capacityBldg: number,
 *    commEnergy: number,
 *    culture: number,
 *    disaster: number,
 *    fireStation: number,
 *    recreation: number,
 *    roadsBridges: number,
 *    shortline: number,
 *    shortsea: number,
 *    solidWaste: number,
 *    sport: number,
 *    transit: number,
 *    tourism: number,
 *    wastewater: number,
 *    water: number
 * }} Reconciliation
 */

/**
 * @typedef {{
 * audited: ReconciliationInformationDataObject,
 * current: ReconciliationInformationDataObject,
 * changes: ReconciliationInformationDataObject,
 * variance: ReconciliationInformationDataObject,
 * isAudited: boolean,
 * hasChanges: boolean
 * }} ReconciliationInformation
 */

/**
 * @typedef {{
 *  flagId: number,
 *  ownerName: string,
 *  lastUpdate: string,
 *  comment: string
 * }} FlagProps
 */

/**
 * @typedef {{
 *  previousValue: number,
 *  currentYearValue: number,
 *  cumulativeValue: number,
 *  flagProps: FlagProps }} FinancialReviewDetails
 */

/**
 * @typedef {{
 *  currentYear: number,
 *  flagProps: FlagProps }} FinancialReviewFlaggableDetails
 */

/**
 * @typedef {{
 *  currentYear: number,
 *  cumulativeTotal: number }} FinancialReviewTransaction
 */

/**
 * @typedef {{
 *  id: number,
 *  name: string }} FinancialReviewFile
 */

/**
 * @typedef {{
 *  broadbandConnectivity: FinancialReviewTransaction,
 *  brownfield: FinancialReviewTransaction,
 *  capacityBldg: FinancialReviewTransaction,
 *  commEnergy: FinancialReviewTransaction,
 *  culture: FinancialReviewTransaction,
 *  disaster: FinancialReviewTransaction,
 *  fireStation: FinancialReviewTransaction,
 *  roadsBridges: FinancialReviewTransaction,
 *  airport: FinancialReviewTransaction,
 *  recreation: FinancialReviewTransaction,
 *  shortline: FinancialReviewTransaction,
 *  shortsea: FinancialReviewTransaction,
 *  solidWaste: FinancialReviewTransaction,
 *  sport: FinancialReviewTransaction,
 *  transit: FinancialReviewTransaction,
 *  tourism: FinancialReviewTransaction,
 *  wastewater: FinancialReviewTransaction,
 *  water: FinancialReviewTransaction }} FinancialReviewExpenditures
 */

/**
 * @typedef {{
 *  id: number,
 *  reviewId: number,
 *  reportingYear: number,
 *  municipalityId: number,
 *  municipalityName: string,
 *  statusId: number,
 *  statusName: string,
 *  assigneeId: number,
 *  assigneeName: string,
 *  openingBalance: FinancialReviewFlaggableDetails,
 *  closingBalance: FinancialReviewFlaggableDetails,
 *  receivedFromAmo: FinancialReviewTransaction,
 *  proceedsAssetDisposal: FinancialReviewDetails,
 *  interestEarned: FinancialReviewDetails,
 *  amountsReceived: FinancialReviewDetails,
 *  amountsTransfered: FinancialReviewDetails,
 *  administrationCosts: FinancialReviewTransaction,
 *  expenditures: FinancialReviewExpenditures,
 *  file: FinancialReviewFile }} FinancialReviewData
 */

/**
 * @typedef {{
 *  projectId: number,
 *  objectId: number,
 *  fieldCode: string,
 *  value: string }} Field
 */

/**
 * @typedef {{
 *  municipalityId: number,
 *  reportingYear: number,
 *  fieldCode: string,
 *  value: string }} FinancialsField
 */

/**
 * @typedef {{
 *  id: number,
 *  projectId: number,
 *  fieldCode: string,
 *  flagText: string }} Flag
 */

/**
 * @typedef {{
 *  reportingStatusId: number,
 *  municipalityId: number,
 *  municipalityName: string,
 *  reportSubmitted: boolean,
 *  financialsApproved: boolean,
 *  projectsApproved: boolean,
 *  certificateApproved: boolean,
 *  selectedForAudit: boolean,
 *  notes: string}} ReportingStatusReview
 */

/**
 * @typedef {{
 *  id: number,
 *  actionTypeId: number,
 *  actionDescription: string,
 *  actionDate: string,
 *  actionBy: string,
 *  isAmoUser: boolean,
 *  actionValue: string }} HistoryItem
 */

/**
 * @typedef {{
 *  id: number,
 *  name: string,
 *  selected: boolean }} Assignee
 */

/**
 * @typedef {{
 *  id: number,
 *  name: string,
 *  actionable: boolean,
 *  selected: boolean }} Status
 */

/**
 * @typedef {{
 *  assignees: Assignee[],
 *  statuses: Status[],
 *  published: boolean }} DataInputLookups
 */

/**
 * @typedef {{
 *  reviewId: number,
 *  assigneeId: number,
 *  statusId: number,
 *  comment: string,
 *  isPublished: boolean,
 *  trackChange: boolean }} DataInputItem
 */

/**
 * @typedef {{
 *  municipalityId: number,
 *  municipalityName: string,
 *  baseAmount: number,
 *  annualTarget: number,
 *  averageAnnualInvestment: number,
 *  statusId: number,
 *  status: string,
 *  ammendmentMade: boolean }} IncrementalityMonitoringReview
 */

/**
 * @typedef {{
 * 	municipalityId: number,
 * 	municipalityName: string,
 *  fileId: number,
 *  fileName: string,
 * 	status: string,
 * 	treasurer: any,
 * 	contactName: string,
 * 	contactEmail: string,
 * 	contactPhone: string,
 * 	institutionName: string,
 * 	lastApprovedInstitutionName: string,
 * 	institutionNumber: string,
 * 	lastApprovedInstitutionNumber: any,
 * 	transitNumber: string,
 * 	lastApprovedTransitNumber: any,
 * 	accountNumber: string,
 * 	lastApprovedAccountNumber: any,
 *  updated: boolean,
 *  updatedDate: string,
 *  isExportedAfterUpdate: boolean}} BankingInformationReview
 */

/**
 * @typedef {{
 * 	municipalityId: number,
 * 	projectId: number,
 *  year: number,
 * 	description: string,
 *  amount: number,
 *  fileId: number,
 * 	authority: string,
 * 	statusReconciliation: string,
 * 	projectCategory: string}} ReconciliationChangesPost
 */

/**
 * @typedef {{
 * 	municipalityId: number,
 * 	financialId: number,
 *  year: number,
 * 	description: string,
 *  amount: number,
 *  fileId: number,
 * 	authority: string,
 * 	statusReconciliation: string,
 * 	financialCategory: string}} ReconciliationChangesFinancialsPost
 */

/**
 * @typedef {{
 * 	municipalityId: number,
 * 	projectId: number,
 *  year: number,
 * 	description: string,
 *  fileId: number,
 * 	authority: string,
 * 	statusReconciliation: string,
 *    projectCategory: string}} ReconciliationChangesPostProjectDeletion
 */

/**
 * @typedef {{
 * 	municipalityId: number,
 * 	projectId: number,
 *  year: number,
 * 	description: string,
 *  fileId: number,
 * 	authority: string,
 * 	statusReconciliation: string,
 *    projectCategory: string,
 *       negative: boolean}} ReconciliationChangesPostProjectRecategorize
 */

const api = new Api("api/amo/review");

/**
 * @param {number} year
 * @returns {Promise<AxiosResponse<FinancialInformation[]>>}
 */
export const getByYear = (year) => api.get(`financials/${year}`);

/**
 * @param {number} year
 * @param {string} type
 * @returns {Promise<AxiosResponse<FinancialInformation[]>>}
 */
export const getQuestionnaires = (year, type) =>
  api.get(`questionnaires/${year}/${type}`);

/**
 * @param {number} year
 * @returns {Promise<AxiosResponse<ReconciliationInformation>>}
 */
export const getReconciliationChanges = (year) =>
  api.get(`reconciliations/changes/${year}`);

/**
 * @param {number} reconciliationId
 * @returns {Promise<AxiosResponse<ReconciliationInformationDataObject>>}
 */
export const getReconciliationChangeById = (reconciliationId) =>
  api.get(`reconciliations/change/${reconciliationId}`);

/**
 * @param {number} year
 * @returns {Promise<AxiosResponse<ReconciliationInformation>>}
 */
export const getReconciliationCurrent = (year) =>
  api.get(`reconciliations/current/${year}`);

/**
 * @param {number} year
 * @returns {Promise<AxiosResponse<ReconciliationInformation>>}
 */
export const getReconciliationCumulative = (year) =>
  api.get(`reconciliations/cumulative/${year}`);

/**
 *
 * @param {number} year
 * @returns {Promise<AxiosResponse>} Empty response
 */
export const postReconciliation = (year) =>
  api.post(`reconciliation/create/${year}`);

/**
 *
 * @param {number} year
 * @returns {Promise<AxiosResponse>} Empty response
 */
export const putReconciliation = (year) =>
  api.put(`reconciliation/update/${year}`);

/**
 *
 * @param {ReconciliationChangesPost} payload
 * @returns {Promise<AxiosResponse>} Empty response
 */
export const postReconciliationChanges = (payload) =>
  api.post(`reconciliationChanges/create`, payload);

/**
 *
 * @param {ReconciliationChangesFinancialsPost} payload
 * @returns {Promise<AxiosResponse>} Empty response
 */
export const postReconciliationChangesFinancials = (payload) =>
  api.post(`reconciliationChanges/financials/create`, payload);

/**
 *
 * @param {ReconciliationChangesPostProjectDeletion} payload
 * @returns {Promise<AxiosResponse>} Empty response
 */
export const postReconciliationChangesByProjectDeletion = (payload) =>
  api.post(`reconciliationChanges/projectDeletion`, payload);

/**
 *
 * @param {ReconciliationChangesPostProjectRecategorize} payload
 * @returns {Promise<AxiosResponse>} Empty response
 */
export const postReconciliationChangesByProjectRecategorize = (payload) =>
  api.post(`reconciliationChanges/projectRecategorize`, payload);

/**
 *
 * @param {ReconciliationChangesPost} payload
 * @returns {Promise<AxiosResponse>} Empty response
 */
export const putReconciliationChanges = (payload) =>
  api.put(`reconciliationChanges/update`, payload);

/**
 * @returns {Promise<AxiosResponse<number[]>>}
 */
export const getYears = () => api.get(`financialYears`);

/**
 * @param {number} municipalityId
 * @param {number} year
 * @returns {Promise<AxiosResponse<FinancialReviewData>>}
 */
export const getFinancialsReview = (municipalityId, year) =>
  api.get(`financials/${municipalityId}/${year}`);

/**
 *
 * @param {number} projectId
 * @param {string} fieldCode
 * @param {Field} payload
 * @returns {Promise<AxiosResponse<number>>} Id of affected flag
 */
export const putField = (projectId, fieldCode, payload) =>
  api.put(`projects/${projectId}/fields/${fieldCode}`, {
    ...payload,
    projectId: +payload.projectId,
    value: `${payload.value}`,
  });

/**
 *
 * @param {number} projectId
 * @param {Flag} payload
 * @returns {Promise<AxiosResponse<number>>} flag id
 */
export const postFlag = (projectId, payload) =>
  api.post(`${projectId}/flags/projects`, payload);

/**
 *
 * @param {number} projectId
 * @param {number} flagId
 * @param {Flag} payload
 * @returns {Promise<AxiosResponse>} Empty response
 */
export const putFlag = (projectId, flagId, payload) =>
  api.put(`${projectId}/flags/projects/${flagId}`, {
    id: flagId,
    ...payload,
  });

/**
 *
 * @param {number} projectId
 * @param {number} flagId
 * @returns {Promise<AxiosResponse>} Empty response
 */
export const deleteFlag = (projectId, flagId) =>
  api.del(`${projectId}/flags/projects/${flagId}`);

export const getReportingStatusModalData = (year, municipalityId) =>
  api.get(`reporting-status/${year}/${municipalityId}`);

export const saveReportingStatusModalData = (year, municipalityId, payload) =>
  api.put(`reporting-status/${year}/${municipalityId}`, payload);

export const getReportingStatusReviews = (year) =>
  api.get(`reporting-status/${year}`);

/**
 *
 * @param {number} municipalityId
 * @param {number} year
 * @param {string} fieldCode
 * @param {FinancialsField} payload
 * @returns {Promise<AxiosResponse<number>>} Id of affected field
 */
export const putFinancialsField = (municipalityId, year, fieldCode, payload) =>
  api.put(`financials/${municipalityId}/${year}/${fieldCode}`, payload);

/**
 *
 * @param {number} objectId
 * @param {Flag} payload
 * @returns {Promise<AxiosResponse<number>>} flag id
 */
export const postFlagFinancials = (objectId, payload) =>
  api.post(`${objectId}/flags/financials`, payload);

/**
 *
 * @param {number} objectId
 * @param {number} flagId
 * @param {Flag} payload
 * @returns {Promise<AxiosResponse>} Empty response
 */
export const putFlagFinancials = (objectId, flagId, payload) =>
  api.put(`${objectId}/flags/financials/${flagId}`, {
    id: flagId,
    ...payload,
  });

/**
 *
 * @param {number} objectId
 * @param {number} flagId
 * @returns {Promise<AxiosResponse>} Empty response
 */
export const deleteFlagFinancials = (objectId, flagId) =>
  api.del(`${objectId}/flags/financials/${flagId}`);

/**
 *
 * @param {number} reviewId
 * @returns {Promise<AxiosResponse<HistoryItem[]>>}
 */
export const getHistoryLogs = (reviewId) => api.get(`${reviewId}/history`);

/**
 *
 * @param {number} reviewId
 * @returns {Promise<AxiosResponse<DataInputLookups>>}
 */
export const getDataInputLookups = (reviewId) => api.get(`${reviewId}/lookups`);

/**
 *
 * @param {number} reviewId
 * @param {DataInputItem} payload
 * @returns {Promise<AxiosResponse>} Empty response
 */
export const putDataInput = (reviewId, payload) =>
  api.put(`${reviewId}`, payload);

/**
 * @returns {Promise<AxiosResponse<IncrementalityMonitoringReview[]>>}
 */
export const getIncrementalityMonitoringReviews = () =>
  api.get("incrementality-monitoring");

/**
 * @returns {Promise<AxiosResponse<BankingInformationReview[]>>}
 */
export const getBankingInformationReviews = () =>
  api.get("banking-information");

/**
 * @returns {Promise<AxiosResponse<NotificationPlaceholder[]>>}
 */
export const getBankingInformationPlaceholders = () =>
  api.get("banking-information-verification-placeholders");

/**
 *
 * @param {number} id
 * @returns {Promise<AxiosResponse<BankingInformationReview[]>>}
 */
export const unlock = (id) => api.put(`banking-information/unlock/${id}`);

/**
 *
 * @param {number} id
 * @returns {Promise<AxiosResponse<BankingInformationReview[]>>}
 */
export const approve = (id) => api.put(`${id}/banking-information/approve`);

/**
 *
 * @param {number} id
 * @returns {Promise<AxiosResponse<BankingInformationReview[]>>}
 */
export const reject = (id) => api.put(`${id}/banking-information/revert`);

/**
 *
 * @param {number} id
 * @returns {Promise<AxiosResponse<BankingInformationReview[]>>}
 */
export const denyUnlock = (id) =>
  api.put(`banking-information/deny-unlock/${id}`);

/**
 *
 * @returns {Promise<AxiosResponse<Assignee[]>>}
 */
export const getAssignees = () => api.get("assignees");
